import { useEffect, useRef, useState } from 'react';
import Quagga from 'quagga';
import { Camera, X, Barcode, MagnifyingGlass } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from './ui/input';
import { Button } from './ui/button';

export function BarcodeScanner({ onDetected, onClose, language = 'es' }) {
  const scannerRef = useRef(null);
  const [active, setActive] = useState(false);
  const [manualInput, setManualInput] = useState('');
  const [lastResult, setLastResult] = useState(null);
  const detectedRef = useRef(new Map());
  // Lightweight i18n: pick English / Spanish / French based on the `language` prop.
  const tx = (en, es, fr) => {
    const lang = String(language || 'es').toLowerCase();
    if (lang.startsWith('fr')) return fr || en;
    if (lang.startsWith('es')) return es || en;
    return en;
  };

  useEffect(() => {
    startScanner();
    return () => stopScanner();
  }, []);

  const startScanner = () => {
    if (!scannerRef.current) return;
    
    Quagga.init({
      inputStream: {
        name: "Live",
        type: "LiveStream",
        target: scannerRef.current,
        constraints: {
          facingMode: "environment",
          width: { ideal: 640 },
          height: { ideal: 480 }
        }
      },
      decoder: {
        readers: [
          "code_128_reader",
          "ean_reader",
          "ean_8_reader",
          "upc_reader",
          "upc_e_reader",
          "code_39_reader"
        ]
      },
      locate: true,
      frequency: 10
    }, (err) => {
      if (err) {
        console.error('Scanner init error:', err);
        return;
      }
      Quagga.start();
      setActive(true);
    });

    Quagga.onDetected((result) => {
      const code = result.codeResult.code;
      if (!code) return;
      
      // Require 3 consistent reads to confirm
      const count = (detectedRef.current.get(code) || 0) + 1;
      detectedRef.current.set(code, count);
      
      if (count >= 3) {
        setLastResult(code);
        stopScanner();
        onDetected(code);
      }
    });
  };

  const stopScanner = () => {
    try { Quagga.stop(); } catch (e) {}
    setActive(false);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (!manualInput.trim()) return;
    stopScanner();
    onDetected(manualInput.trim().toUpperCase());
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center p-4"
    >
      {/* Header */}
      <div className="text-center text-white mb-4">
        <Barcode size={36} className="mx-auto mb-2" />
        <h2 className="text-xl font-bold tracking-wide" style={{ fontFamily: 'Bebas Neue' }}>
          {tx('SCAN BARCODE', 'ESCANEAR CODIGO', 'SCAN BARCODE')}
        </h2>
        <p className="text-white/50 text-sm">
          {tx('Point camera at barcode', 'Apunta la camara al codigo de barras', 'Point camera at barcode')}
        </p>
      </div>

      {/* Scanner viewport */}
      <div className="relative w-80 h-60 rounded-xl overflow-hidden bg-gray-900 mb-4">
        <div ref={scannerRef} className="w-full h-full" data-testid="barcode-scanner-viewport">
          {/* Quagga renders video here */}
        </div>
        
        {/* Scan line animation */}
        {active && (
          <motion.div
            className="absolute left-4 right-4 h-0.5 bg-[#0077FF] shadow-[0_0_8px_#0077FF]"
            animate={{ top: ['20%', '80%', '20%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          />
        )}
        
        {/* Corner markers */}
        <div className="absolute top-2 left-2 w-6 h-6 border-t-2 border-l-2 border-[#0077FF] rounded-tl" />
        <div className="absolute top-2 right-2 w-6 h-6 border-t-2 border-r-2 border-[#0077FF] rounded-tr" />
        <div className="absolute bottom-2 left-2 w-6 h-6 border-b-2 border-l-2 border-[#0077FF] rounded-bl" />
        <div className="absolute bottom-2 right-2 w-6 h-6 border-b-2 border-r-2 border-[#0077FF] rounded-br" />
        
        {lastResult && (
          <div className="absolute inset-0 bg-green-500/20 flex items-center justify-center">
            <p className="bg-green-500 text-white px-4 py-2 rounded-lg font-mono font-bold">{lastResult}</p>
          </div>
        )}
      </div>

      {/* Manual input */}
      <div className="w-80 bg-white/10 backdrop-blur-sm rounded-xl p-4">
        <p className="text-white/70 text-xs mb-2">
          {tx('Or enter code manually:', 'O ingresa el codigo manualmente:', 'Or enter code manually:')}
        </p>
        <form onSubmit={handleManualSubmit} className="flex gap-2">
          <Input
            value={manualInput}
            onChange={(e) => setManualInput(e.target.value.toUpperCase())}
            placeholder={tx('Tracking number...', 'Numero de rastreo...', 'Tracking number...')}
            className="flex-1 rounded-lg bg-white/90"
            data-testid="scanner-manual-input"
          />
          <Button type="submit" className="ship-gradient text-white rounded-lg" data-testid="scanner-manual-submit">
            <MagnifyingGlass size={18} />
          </Button>
        </form>
      </div>

      {/* Close button */}
      <Button
        onClick={() => { stopScanner(); onClose(); }}
        variant="outline"
        className="mt-4 rounded-lg text-white border-white/30 hover:bg-white/10"
        data-testid="close-scanner-btn"
      >
        <X size={18} className="mr-2" />
        {tx('Close', 'Cerrar', 'Fermer')}
      </Button>
    </motion.div>
  );
}
